import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, RotateCcw, Eye, Moon, Volume2, VolumeX } from 'lucide-react';
import { useI18n } from '../lib/i18n';

const FONT_SIZES = [1, 1.125, 1.25, 1.375];
const STORAGE_KEY = 'ibrc-a11y';

const A11ySidebar = () => {
  const [a11yOpen, setA11yOpen] = useState(false);
  const [vlibrasOpen, setVlibrasOpen] = useState(false);
  const [fontLevel, setFontLevel] = useState(0);
  const [highContrast, setHighContrast] = useState(false);
  const [invertColors, setInvertColors] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const { lang } = useI18n();
  const pt = lang === 'pt';

  // Load saved preferences
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (saved.fontLevel) { setFontLevel(saved.fontLevel); document.documentElement.style.setProperty('--font-scale', FONT_SIZES[saved.fontLevel]); }
      if (saved.highContrast) { setHighContrast(true); document.documentElement.classList.add('high-contrast'); }
      if (saved.invertColors) { setInvertColors(true); document.documentElement.classList.add('invert-colors'); }
    } catch {}
  }, []);

  const save = useCallback((updates) => {
    const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...updates }));
  }, []);

  const announce = (msg) => {
    const el = document.getElementById('a11y-live');
    if (el) { el.textContent = ''; setTimeout(() => { el.textContent = msg; }, 100); }
  };

  const changeFontSize = (dir) => {
    const next = dir > 0 ? Math.min(fontLevel + 1, FONT_SIZES.length - 1) : Math.max(fontLevel - 1, 0);
    setFontLevel(next);
    document.documentElement.style.setProperty('--font-scale', FONT_SIZES[next]);
    save({ fontLevel: next });
  };

  const toggleHighContrast = () => {
    const next = !highContrast;
    setHighContrast(next);
    document.documentElement.classList.toggle('high-contrast', next);
    save({ highContrast: next });
  };

  const toggleInvertColors = () => {
    const next = !invertColors;
    setInvertColors(next);
    document.documentElement.classList.toggle('invert-colors', next);
    save({ invertColors: next });
  };

  const toggleSpeech = () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      const text = document.querySelector('main')?.textContent || '';
      if (!text.trim()) return;
      const u = new SpeechSynthesisUtterance(text.substring(0, 5000));
      u.lang = pt ? 'pt-BR' : 'en-US';
      u.rate = 0.9;
      u.onend = () => setIsSpeaking(false);
      window.speechSynthesis.speak(u);
      setIsSpeaking(true);
    }
  };

  const resetAll = () => {
    setFontLevel(0); setHighContrast(false); setInvertColors(false); setIsSpeaking(false);
    window.speechSynthesis.cancel();
    document.documentElement.style.removeProperty('--font-scale');
    document.documentElement.classList.remove('high-contrast', 'invert-colors');
    localStorage.removeItem(STORAGE_KEY);
  };

  // VLibras is loaded via index.html — toggle its native button
  const toggleVlibras = () => {
    const btn = document.querySelector('[vw-access-button]');
    if (btn) btn.click();
    setVlibrasOpen((prev) => !prev);
  };

  return (
    <>
      <div id="a11y-live" role="status" aria-live="polite" aria-atomic="true" className="sr-only" />

      {/* Two sidebar buttons — vertically centered on the right edge */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-[100] flex flex-col" style={{ gap: '2px' }}>
        {/* VLibras toggle button — always shows hand icon */}
        <button
          onClick={toggleVlibras}
          aria-label={vlibrasOpen ? 'Fechar VLibras' : 'VLibras — Libras'}
          className={`w-[42px] h-[42px] ${vlibrasOpen ? 'bg-[#0a6dd6]' : 'bg-[#0855B1]'} hover:bg-[#0a6dd6] transition-colors flex items-center justify-center rounded-l-md`}
          title="VLibras"
        >
          <svg width="24" height="24" viewBox="0 0 48 48" fill="white" aria-hidden="true">
            <path d="M24 4c-1.1 0-2 .9-2 2v14c0 .6-.4 1-1 1s-1-.4-1-1V8c0-1.1-.9-2-2-2s-2 .9-2 2v12c0 .6-.4 1-1 1s-1-.4-1-1v-8c0-1.1-.9-2-2-2s-2 .9-2 2v14c0 8.8 7.2 16 16 16s16-7.2 16-16V14c0-1.1-.9-2-2-2s-2 .9-2 2v6c0 .6-.4 1-1 1s-1-.4-1-1V8c0-1.1-.9-2-2-2s-2 .9-2 2v12c0 .6-.4 1-1 1s-1-.4-1-1V6c0-1.1-.9-2-2-2z"/>
          </svg>
        </button>

        {/* Accessibility button */}
        {!a11yOpen ? (
          <button
            onClick={() => setA11yOpen(true)}
            aria-label={pt ? 'Acessibilidade' : 'Accessibility'}
            className="w-[42px] h-[42px] bg-[#005EA5] hover:bg-[#0074cc] transition-colors flex items-center justify-center rounded-l-md"
            title={pt ? 'Acessibilidade' : 'Accessibility'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="4" r="2" />
              <path d="M12 8v4m0 0l-4 6m4-6l4 6" />
              <path d="M6 12h12" />
            </svg>
          </button>
        ) : (
          <button
            onClick={() => setA11yOpen(false)}
            aria-label="Fechar acessibilidade"
            className="w-[42px] h-[42px] bg-[#005EA5] hover:bg-[#0074cc] transition-colors flex items-center justify-center rounded-l-md"
          >
            <X size={16} color="white" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Accessibility Panel */}
      <AnimatePresence>
        {a11yOpen && (
          <motion.aside
            aria-label={pt ? 'Ferramentas de acessibilidade' : 'Accessibility tools'}
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 300, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed top-1/2 -translate-y-1/2 right-[44px] z-[89] w-[220px] bg-white/95 backdrop-blur-xl border border-black/10 shadow-2xl rounded-l-xl overflow-hidden"
          >
            <div className="px-4 py-3 bg-[#005EA5] flex items-center justify-between">
              <span className="font-oswald text-[11px] uppercase tracking-wider text-white">
                {pt ? 'Acessibilidade' : 'Accessibility'}
              </span>
              <button onClick={() => setA11yOpen(false)} className="text-white/60 hover:text-white" aria-label="Fechar">
                <X size={14} aria-hidden="true" />
              </button>
            </div>
            <div className="p-2 space-y-0.5">
              {[
                { icon: Plus, label: pt ? 'Aumentar texto' : 'Increase text', action: () => changeFontSize(1), active: fontLevel > 0 },
                { icon: Minus, label: pt ? 'Diminuir texto' : 'Decrease text', action: () => changeFontSize(-1), active: false },
                { icon: Eye, label: pt ? 'Alto contraste' : 'High contrast', action: toggleHighContrast, active: highContrast },
                { icon: Moon, label: pt ? 'Inverter cores' : 'Invert colors', action: toggleInvertColors, active: invertColors },
                { icon: isSpeaking ? VolumeX : Volume2, label: pt ? (isSpeaking ? 'Parar leitura' : 'Ler página') : (isSpeaking ? 'Stop' : 'Read page'), action: toggleSpeech, active: isSpeaking },
                { icon: RotateCcw, label: pt ? 'Redefinir' : 'Reset', action: resetAll, active: false },
              ].map((tool, i) => (
                <button key={i} onClick={tool.action}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-[12px] font-manrope transition-all ${
                    tool.active ? 'bg-[#005EA5]/10 text-[#005EA5] font-semibold' : 'text-black/60 hover:bg-black/5'
                  }`}>
                  <tool.icon size={14} aria-hidden="true" className="flex-shrink-0" />
                  {tool.label}
                </button>
              ))}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

export default A11ySidebar;
